/**
 * Journey availability — real slots, from the op that already exists.
 *
 * Replaces the three fabricated collections that used to live in
 * journey-model.ts (`slotDays`, `slotTimes`, `staffFree`) — 15 hardcoded July
 * 2026 dates, a positional time array, and a hash that invented per-staff
 * freeness. Those violated the seed/mock rule (sample data belongs behind the
 * API boundary, never in app/src) and could not hot-swap: mounted on the real
 * data layer the time screen rendered July 2026 whatever the tenant said.
 *
 * Source of truth is `getPublicJourneyAvailability` — GET
 * /v1/journeys/public/{slug}/availability, the canonical op behind the
 * `listApptSlots` concierge alias (contract: api-contracts/src/journey.ts:1306,
 * ruling in CC-API-ANSWER-journey-availability-2026-08-14.md). No new contract
 * was needed; an earlier pass of this module briefly went through
 * `searchAvailability` (POST /v1/public/availability/search) before that answer
 * was read — this is the corrected wiring.
 *
 * Four rules from that answer, all load-bearing here:
 *
 *  - PRESENT = FREE, PER STAFF. A slot exists in days[d].slots for staffId X if
 *    and only if X is free then. Absence is busy — off-schedule, on a break,
 *    a facility closure, or already booked.
 *  - `date` IS THE ONLY DATE FIELD WE CONSUME. `dow` / `d` / `month` are
 *    deprecated on the DTO (they are English display prose emitted from the API,
 *    unreachable by either side's catalogs). `label`, `short` and `dow` are
 *    derived here, and "Today · " is a comparison, not a pinned string.
 *  - ADJACENCY IS `a.endUtc === b.startUtc`, never `index + 1`. Two entries next
 *    to each other in a day's slot list are not necessarily adjacent in time —
 *    true even on a regular grid, the moment one slot is taken.
 *  - `full` IS NOT DERIVABLE FROM EMPTINESS. `full === true` means sold out; the
 *    key ABSENT means the distinction cannot be made (closed, or outside the
 *    booking window) and must render as *not bookable*, which is the weaker
 *    claim. So this module reports `full` and `empty` separately and never
 *    promotes one to the other.
 *
 * The time axis is built from each slot's own `time` — already local to the
 * venue, and guaranteed zero-padded 24-hour "HH:MM" by a single formatter
 * (workers/_shared/zoned-time.ts:113, with the Intl `'24'`-hour trap closed at
 * :37, so midnight is "00:00"). Nothing is re-derived from `startUtc`: the DTO
 * carries no timezone and guessing one would move slots across midnight.
 */
import React from "react";
import { getPublicJourneyAvailability } from "@v0y4ge/api-client";
import type { GetPublicJourneyAvailabilityResponse } from "@v0y4ge/api-contracts";

type Payload = GetPublicJourneyAvailabilityResponse;
type WireDay = Payload["data"]["days"][number];
type WireSlot = WireDay["slots"][number];

export interface AvailDay {
  /** Stable index into `days` — array position is the renderer's day handle. */
  id: number;
  /** YYYY-MM-DD, as served: the only date field consumed from the op. */
  iso: string;
  /** Calendar parts, derived from `iso`, for the month grid. */
  date: { y: number; m: number; d: number };
  label: string;
  short: string;
  dow: string;
  /** No slot for anyone on this day. Says nothing about WHY. */
  empty: boolean;
  /** Sold out, as declared by the API. Absent flag → false, never inferred. */
  full: boolean;
}

export interface AvailabilityGrid {
  days: AvailDay[];
  /** Distinct local start times across the range, "HH:MM" 24h, ascending. */
  times: string[];
  loading: boolean;
  error: string | null;
  /** Is this staff member free at (day, time)? Present = free. */
  free(staffId: string, dayIdx: number, timeIdx: number): boolean;
  /** Free at (day, time) AND at the slot that starts when that one ends. */
  chain(staffId: string, dayIdx: number, timeIdx: number): boolean;
  /** Time index of the nth consecutive slot from (day, time), or -1. */
  step(staffId: string, dayIdx: number, timeIdx: number, n: number): number;
  /** The UTC instant a cell starts at, or null when the cell is empty. */
  startUtc(dayIdx: number, timeIdx: number): string | null;
  /** Earliest free cell, optionally for one staff member. */
  firstFree(staffId?: string | null): { day: number; time: string } | null;
}

interface Cell {
  startUtc: string;
  /** staffId → endUtc. */
  byStaff: Map<string, string>;
}

const NO_DAYS: AvailDay[] = [];
const NO_TIMES: string[] = [];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** YYYY-MM-DD → calendar parts, read as a plain date (no zone shift). */
function parseISODate(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = String(iso).split("-").map(Number);
  return { y: y || 0, m: (m || 1) - 1, d: d || 1 };
}

function isoAddDays(iso: string, n: number): string {
  const p = parseISODate(iso);
  return new Date(Date.UTC(p.y, p.m, p.d + n)).toISOString().slice(0, 10);
}

function todayISO(): string {
  const now = new Date();
  const p = [now.getFullYear(), now.getMonth() + 1, now.getDate()];
  return `${p[0]}-${String(p[1]).padStart(2, "0")}-${String(p[2]).padStart(2, "0")}`;
}

function dayLabels(iso: string, today: string) {
  const p = parseISODate(iso);
  const dow = DOW_SHORT[new Date(Date.UTC(p.y, p.m, p.d)).getUTCDay()];
  const stem = `${dow} ${p.d} ${MONTHS_SHORT[p.m]}`;
  return { dow, short: `${dow} ${p.d}`, label: iso === today ? `Today · ${stem}` : stem, date: p };
}

/** What every lookup answers before a payload lands. */
function emptyGrid(loading: boolean, error: string | null): AvailabilityGrid {
  return {
    days: NO_DAYS, times: NO_TIMES, loading, error,
    free: () => false,
    chain: () => false,
    step: () => -1,
    startUtc: () => null,
    firstFree: () => null,
  };
}

function buildGrid(wire: Payload | null, loading: boolean, error: string | null): AvailabilityGrid {
  if (!wire) return emptyGrid(loading, error);
  const served = wire.data?.days ?? wire.data?.availability ?? [];
  const today = todayISO();

  const startOf = (s: WireSlot) => s.startUtc ?? s.start_utc ?? null;
  const endOf = (s: WireSlot) => s.endUtc ?? s.end_utc ?? null;
  const staffOf = (s: WireSlot) => s.staffId ?? s.staff_id ?? null;

  // Pass 1 — the time axis: every distinct local start across the range. `time`
  // is served as zero-padded 24-hour "HH:MM", so it sorts and slotMin() parses.
  const timeSet = new Set<string>();
  for (const day of served) for (const s of day.slots ?? []) if (s.time) timeSet.add(s.time);
  const times = [...timeSet].sort();
  const axis = new Map(times.map((t, i) => [t, i]));

  // Pass 2 — the cells, plus a startUtc → (day, time) index for adjacency.
  const cells = new Map<string, Cell>();
  const atInstant = new Map<string, { d: number; t: number }>();
  const days: AvailDay[] = served.map((day, d) => {
    let any = false;
    for (const s of day.slots ?? []) {
      const t = axis.get(s.time || "");
      const start = startOf(s), end = endOf(s), staffId = staffOf(s);
      if (t == null || !start || !staffId) continue;
      const key = `${d}|${t}`;
      let cell = cells.get(key);
      if (!cell) { cell = { startUtc: start, byStaff: new Map() }; cells.set(key, cell); }
      if (end) cell.byStaff.set(staffId, end);
      atInstant.set(start, { d, t });
      any = true;
    }
    // `full` only when the API says so. An empty day with no flag is NOT full.
    return { id: d, iso: day.date, empty: !any, full: day.full === true, ...dayLabels(day.date, today) };
  });

  const cellAt = (d: number, t: number) => cells.get(`${d}|${t}`);
  const free = (staffId: string, d: number, t: number) => !!cellAt(d, t)?.byStaff.has(staffId);
  const nextIdx = (staffId: string, d: number, t: number): number => {
    const end = cellAt(d, t)?.byStaff.get(staffId);
    if (!end) return -1;
    const at = atInstant.get(end);
    // Same day, and free there too — otherwise this staff member's run ends.
    if (!at || at.d !== d || !free(staffId, at.d, at.t)) return -1;
    return at.t;
  };
  const step = (staffId: string, d: number, t: number, n: number): number => {
    let cur = free(staffId, d, t) ? t : -1;
    for (let i = 0; i < n && cur >= 0; i++) cur = nextIdx(staffId, d, cur);
    return cur;
  };

  return {
    days, times, loading, error,
    free,
    chain: (staffId, d, t) => step(staffId, d, t, 1) >= 0,
    step,
    startUtc: (d, t) => cellAt(d, t)?.startUtc ?? null,
    firstFree: (staffId) => {
      for (let d = 0; d < days.length; d++) {
        for (let t = 0; t < times.length; t++) {
          const cell = cellAt(d, t);
          if (!cell || cell.byStaff.size === 0) continue;
          if (!staffId || staffId === "any" || cell.byStaff.has(staffId)) return { day: d, time: times[t] };
        }
      }
      return null;
    },
  };
}

export interface UseAvailabilityParams {
  /** The journey's public slug — the op is slug-scoped. */
  slug?: string | null;
  serviceId?: string | null;
  staffId?: string | null;
  /** Range length in days, from today. */
  days?: number;
}

/**
 * One fetch per (slug, service, staff, range) at the renderer boundary — the
 * whole grid the time screen paints from, pivoted once for the synchronous
 * per-cell lookups it does during paint. Empty until a payload lands: no
 * placeholder days, no invented slots.
 */
export function useJourneyAvailability(params: UseAvailabilityParams): AvailabilityGrid {
  const { slug, serviceId, staffId, days = 28 } = params;
  const [state, setState] = React.useState<{ wire: Payload | null; loading: boolean; error: string | null }>({ wire: null, loading: !!slug, error: null });

  React.useEffect(() => {
    if (!slug) { setState({ wire: null, loading: false, error: null }); return; }
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    const from = todayISO();
    getPublicJourneyAvailability(slug, {
      dateFrom: from,
      dateTo: isoAddDays(from, Math.max(1, days) - 1),
      ...(serviceId ? { serviceId } : {}),
      // "any" is a renderer sentinel, never a staff id — it must not reach the query.
      ...(staffId && staffId !== "any" ? { staffId } : {}),
    })
      .then((wire) => { if (live) setState({ wire, loading: false, error: null }); })
      .catch((e: unknown) => { if (live) setState({ wire: null, loading: false, error: e instanceof Error ? e.message : "Availability unavailable" }); });
    return () => { live = false; };
  }, [slug, serviceId, staffId, days]);

  return React.useMemo(() => buildGrid(state.wire, state.loading, state.error), [state.wire, state.loading, state.error]);
}
